/* PortalLandingIntro — branded splash that plays every time a user lands
 * on a portal entrance (`*-login` route) in production.
 *
 * Why it exists: After iter98.19 the patient/doctor/admin/COE login screens
 * live on a different hostname (`my.invisique.in`) than the marketing site
 * (`www.invisique.in`). Without an intro the cross-host hop feels abrupt —
 * the user clicks "Patient Portal" on marketing and just sees a bare
 * `<Login />` card. This component covers that hop with a ~1.2s
 * gradient-sweep splash featuring the Invisique mark + role-aware overline
 * + brand tagline, then fades out to reveal the login card underneath
 * (mounted the whole time, so it's immediately interactive when the
 * overlay clears).
 *
 * Play contract (per iter98.32 user choice — "play every time"):
 *   • Plays on EVERY visit to a `*-login` route, regardless of host. The
 *     component is only mounted on the 4 `*-login` routes (see App.js),
 *     so the route itself is the gate.
 *   • Pass `?intro=0` to suppress (for QA / automated screenshots).
 *   • If a previous user signed in on this browser within the last 90
 *     days AND their role matches the current login route, the tagline
 *     swaps to "Welcome back, {first-name}" — a small continuity touch.
 *
 * Animation timeline (CSS-driven, no framer-motion overhead for a 1.2s
 * one-shot animation):
 *     0ms — overlay opaque, content hidden
 *    80ms — logo card pops in (scale 0.6 → 1, spring)
 *   180ms — role overline pill rises (PATIENT / PROVIDER / etc.)
 *   340ms — "Invisique™" wordmark fades + rises
 *   500ms — tagline fades + rises
 *   840ms — overlay starts fading out
 *  1200ms — overlay removed from DOM
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

/* Role-aware micro-greeting copy. Pulled off `location.pathname` so the
 * splash feels personalised to whichever portal door you came in through. */
const ROLE_OVERLINE = {
    "/patient-login": "Patient Portal",
    "/doctor-login":  "Provider Portal",
    "/admin-login":   "Admin · Command Center",
    "/coe-login":     "Center of Excellence",
    "/sales-login":   "Sales · Command Center",
};

/* iter98.79 — Per-portal glint colour. Each portal door gets its own
 * signature highlight tint on the aligner rim trace so a returning
 * user gets a "you've come to the right door" cue from the first 600ms
 * of the splash. Values are CSS-friendly hsl() strings consumed by the
 * `--portal-intro-glint` custom property declared on the overlay. */
const ROLE_TINT = {
    "/patient-login": "mint",     // soft mint — patient dashboard accent
    "/doctor-login":  "gold",     // warm gold — premium provider feel
    "/admin-login":   "cyan",     // cool cyan — operational
    "/coe-login":     "cyan",     // shared with admin — operational
    "/sales-login":   "coral",    // coral — outreach / energy
};

/* Roles that we'll personalise for, keyed by the login route. The values are
 * the `user.role` strings the backend returns at sign-in. We allow multiple
 * because e.g. clinic_staff also funnel through `/doctor-login`. If a stored
 * last-user role isn't in the matching list, we fall back to the generic
 * tagline so we don't show "Welcome back, …" to the wrong audience. */
const ROUTE_PERSONALISE = {
    "/patient-login": ["patient"],
    "/doctor-login":  ["doctor", "clinic_staff"],
    "/admin-login":   ["admin"],
    "/coe-login":     ["staff"],
};

/* How long a personalised greeting stays warm. After 90 days we treat the
 * hint as stale and show the generic tagline instead. */
const PERSONAL_GREETING_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function readLastUserGreeting(pathname) {
    try {
        const raw = window.localStorage.getItem("invisique_last_user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.greet || !parsed?.role) return null;
        const allowed = ROUTE_PERSONALISE[pathname];
        if (!allowed || !allowed.includes(parsed.role)) return null;
        if (parsed.ts && Date.now() - parsed.ts > PERSONAL_GREETING_TTL_MS) return null;
        return parsed.greet;
    } catch { return null; }
}

function shouldPlay({ force }) {
    if (typeof window === "undefined") return false;
    if (force) return true;
    // The splash plays on every visit to a portal-login route, regardless of
    // host. `PortalLandingIntro` is only mounted on the 4 `*-login` routes
    // (see App.js), so the route itself is the gate — no extra host check
    // is needed. Pass `?intro=0` to suppress for QA / automated screenshots.
    try {
        const optOut = new URLSearchParams(window.location.search).get("intro") === "0";
        if (optOut) return false;
    } catch { /* noop */ }
    return true;
}

export default function PortalLandingIntro({ children }) {
    const location = useLocation();
    const overline = ROLE_OVERLINE[location.pathname] || "Invisique™ Portal";
    /* iter98.79 — Map the current portal route to a glint tint slug
     * ("mint" / "gold" / "cyan" / "coral"). The overlay reads this via
     * a `data-tint` attribute; CSS attribute selectors swap the
     * `--portal-intro-glint` custom property so the rim trace catches
     * the right colour for the portal the user just walked into. */
    const tint = ROLE_TINT[location.pathname] || "neutral";
    // Personalised "Welcome back, Dr. Wilson" hint — null if we don't have a
    // matching last-user stashed for this login route. Computed once on
    // mount (lazy initializer) so re-renders during the 1.2s splash don't
    // re-read localStorage. Memoised across the splash lifetime.
    const [personalGreet] = useState(() => {
        if (typeof window === "undefined") return null;
        return readLastUserGreeting(location.pathname);
    });

    // Compute the initial phase synchronously in the lazy useState
    // initializer. This runs exactly once on mount and decides whether the
    // splash should play. We deliberately don't gate on auth state here —
    // `shouldPlay` uses session storage + portal-mode + URL params only,
    // and protected routes downstream redirect signed-in visitors well
    // inside the splash window so they never see a visible end-state.
    const [phase, setPhase] = useState(() => {
        if (typeof window === "undefined") return "done";
        const force = new URLSearchParams(window.location.search).get("intro") === "1";
        return shouldPlay({ force }) ? "playing" : "done";
    });

    // Once we enter "playing", schedule the one-shot transition to "done"
    // that matches the CSS fade-out length. No sessionStorage write anymore
    // (iter98.22) — the splash now plays on every visit per user choice.
    useEffect(() => {
        if (phase !== "playing") return;
        const t = setTimeout(() => setPhase("done"), 1200);
        return () => clearTimeout(t);
    }, [phase]);

    // Note: signed-in users hitting a `*-login` route will see the splash
    // briefly before `ProtectedRoute` / role-redirect logic carries them
    // into their dashboard (~50–100ms typical), so the visible portion of
    // the overlay is negligible for them.

    return (
        <>
            {children}
            {phase === "playing" && (
                <div className="portal-intro-overlay" data-testid="portal-intro-overlay" data-tint={tint} aria-hidden="true">
                    <div className="portal-intro-sweep" />
                    <div className="portal-intro-content">
                        <div className="portal-intro-mark">
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path
                                    d="M12 2 L13.6 8.5 L20 10 L13.6 11.5 L12 18 L10.4 11.5 L4 10 L10.4 8.5 Z"
                                    fill="white"
                                />
                                <circle cx="19" cy="5" r="1.6" fill="white" />
                                <circle cx="5" cy="19" r="1.2" fill="white" opacity="0.85" />
                            </svg>
                        </div>
                        <div className="portal-intro-overline" data-testid="portal-intro-overline">
                            <span className="portal-intro-overline-dot" />
                            {overline}
                            <span className="portal-intro-overline-dot" />
                        </div>
                        <div className="portal-intro-wordmark">
                            Invisique™<span className="portal-intro-tm">™</span>
                        </div>
                        <div
                            className="portal-intro-tagline"
                            data-testid={personalGreet ? "portal-intro-personal-greeting" : "portal-intro-tagline"}
                        >
                            {personalGreet
                                ? `Welcome back, ${personalGreet}`
                                : "Invisible Precision · Uniquely Yours"}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
